// Ispisi servisnog naloga: ponuda za servis, nalog za servis i uski POS ispis.
//
// ZASTO zasebno, a ne u DocumentPrintSales: servisni nalog se ispisuje na tri
// nacina, a sva tri dijele jedno pravilo - USLUGE i ROBA idu u odvojene
// tablice. U autoservisu to su dva razlicita posla: rad majstora i ugradjeni
// dijelovi. Dosadasnji ispis ih je mijesao u jedan popis.
//
// Usluga je stavka kojoj je "itemService" postavljen (oznaka dolazi s artikla).
// Artikl koji nije oznacen kao usluga racuna se kao roba - zato se na ispisu
// uvijek vidi broj stavaka po skupini, da se odmah primijeti ako artikli nisu
// oznaceni.
//
// "Nalog za servis" ima iza svake skupine PRAZNE REDOVE: serviser rukom dopise
// sto je stvarno odradio i koji je dio ugradio, pa se nalog poslije prepise u
// program. Zato taj ispis ide bez iznosa po stavci ako se cijene skrivaju.

import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import dayjs from 'dayjs'

const LINIJA = '#9e9e9e'
const SIVA = '#666666'

const num = (v) => {
  const n = parseFloat(v)
  return isNaN(n) ? 0 : n
}
const novac = (v) => num(v).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
// Kolicina se pise na tri decimale samo kad ih stvarno ima (tonaza, m2).
const kolicina = (v) => {
  const n = num(v)
  const s = Math.abs(n - Math.round(n)) < 0.0005 ? n.toFixed(0) : n.toFixed(3)
  return s.replace('.', ',')
}
const datum = (d) => (d ? dayjs(d).format('DD.MM.YYYY') : '')
const tekst = (v) => (v === null || v === undefined ? '' : String(v))

// Servisni nalog cuva svoje podatke u opisnim poljima dokumenta - tako je
// napravljen od pocetka, posebnih stupaca nema:
//   documentDescription4 - PREDMET servisa (sto se servisira) - kljucni podatak
//   documentDescription1 - prijavljeni problem
//   documentDescription5 - opis servisa (sto je odradjeno)
//   status               - status servisa (Reported, Received, InProgress...)
//   deliveryCurrier      - serviser
const PREDMET = 'documentDescription4'
const PROBLEM = 'documentDescription1'
const ODRADJENO = 'documentDescription5'

const STATUSI = {
  Reported: 'Prijavljeno',
  Received: 'Zaprimljeno',
  InProgress: 'U tijeku',
  Done: 'Završeno',
  Delivered: 'Preuzeto'
}
const statusTekst = (d) => STATUSI[tekst(d.status)] || tekst(d.status)

/** Usluge i roba - po oznaci s artikla. */
export const podijeliStavke = (stavke) => {
  const sve = Array.isArray(stavke) ? stavke : []
  return {
    usluge: sve.filter(s => s.itemService === true),
    roba: sve.filter(s => s.itemService !== true)
  }
}

const zbroj = (stavke) => stavke.reduce((z, s) => z + num(s.itemAmountWithVAT !== undefined && s.itemAmountWithVAT !== null
  ? s.itemAmountWithVAT
  : num(s.itemPriceWithVAT) * num(s.itemPcsMinus || s.itemPcsPlus || 0)), 0)

/** Iznos jedne stavke - isti izvor kao zbroj. */
const iznosStavke = (s) => (s.itemAmountWithVAT !== undefined && s.itemAmountWithVAT !== null
  ? num(s.itemAmountWithVAT)
  : num(s.itemPriceWithVAT) * num(s.itemPcsMinus || s.itemPcsPlus || 0))

const kol = (s) => num(s.itemPcsMinus) || num(s.itemPcsPlus) || 0

// ---------------------------------------------------------------------------
// A4: zajednicki dijelovi ponude i naloga
// ---------------------------------------------------------------------------

const zaglavljeA4 = (ctx) => {
  const c = ctx.company || {}
  const d = ctx.document || {}
  return {
    columns: [
      {
        width: '*',
        stack: [
          {text: c.CompanyName || c.CompanyNameShort || '', fontSize: 13, bold: true},
          {text: [c.CompanyStreet, c.CompanyBuildingNameNumber].filter(x => x).join(' '), fontSize: 8},
          {text: [c.CompanyPostcode, c.CompanyCity].filter(x => x).join(' '), fontSize: 8},
          {text: c.CompanyVATID ? 'OIB: ' + c.CompanyVATID : '', fontSize: 8},
          {text: [c.CompanyPhones, c.Companyemail].filter(x => x).join('  ·  '), fontSize: 8, color: SIVA}
        ]
      },
      {
        width: 'auto',
        stack: [
          {text: ctx.naslov, fontSize: 16, bold: true, alignment: 'right'},
          {text: 'Broj: ' + tekst(d.documentId), fontSize: 11, alignment: 'right'},
          {text: 'Datum: ' + datum(d.documentDate), fontSize: 9, alignment: 'right'},
          {text: d.documentTime ? 'Vrijeme: ' + String(d.documentTime).substr(0, 5) : '',
            fontSize: 9, alignment: 'right'}
        ]
      }
    ],
    margin: [0, 0, 0, 10]
  }
}

const partnerA4 = (ctx) => {
  const d = ctx.document || {}
  const adresa = [[d.partnerStreet, d.partnerBuildingNameNumber].filter(x => x).join(' '),
    [d.partnerPostcode, d.partnerCity].filter(x => x).join(' ')].filter(x => x).join(', ')
  return {
    table: {
      widths: ['*', 'auto'],
      body: [[
        {
          stack: [
            {text: 'KUPAC', fontSize: 7, color: SIVA},
            {text: d.partnerName || '', fontSize: 11, bold: true},
            {text: adresa, fontSize: 9},
            {text: d.partnerVATID ? 'OIB: ' + d.partnerVATID : '', fontSize: 9},
            {text: d.partnerPhones ? 'Tel: ' + d.partnerPhones : '', fontSize: 9}
          ],
          border: [false, false, false, false]
        },
        {
          stack: [
            {text: statusTekst(d) ? 'STATUS' : '', fontSize: 7, color: SIVA},
            {text: statusTekst(d), fontSize: 10, bold: true},
            {text: d.deliveryCurrier ? 'Servis: ' + d.deliveryCurrier : '', fontSize: 9}
          ],
          alignment: 'right',
          border: [false, false, false, false]
        }
      ]]
    },
    layout: 'noBorders',
    margin: [0, 0, 0, 8]
  }
}

/**
 * Predmet servisa - sto se servisira. Stoji istaknuto iznad stavaka: bez toga
 * se s papira ne zna o cemu je rijec, pa je to na servisnom nalogu prvi podatak
 * koji se gleda.
 */
const predmetServisa = (ctx) => {
  const d = ctx.document || {}
  const predmet = tekst(d[PREDMET]).trim()
  if (!predmet) { return null }
  return {
    table: {
      widths: ['*'],
      body: [[{
        stack: [
          {text: 'PREDMET SERVISA', fontSize: 7, color: SIVA},
          {text: predmet, fontSize: 12, bold: true}
        ],
        margin: [6, 5, 6, 5]
      }]]
    },
    layout: {
      hLineWidth: () => 0.8, vLineWidth: () => 0.8,
      hLineColor: () => LINIJA, vLineColor: () => LINIJA
    },
    margin: [0, 0, 0, 8]
  }
}

/** Prijavljeni problem i odradjeno - oba iz opisnih polja naloga. */
const opisServisa = (ctx) => {
  const d = ctx.document || {}
  const redovi = []
  if (tekst(d[PROBLEM]).trim()) {
    redovi.push({text: 'Prijavljeni problem', fontSize: 8, color: SIVA})
    redovi.push({text: tekst(d[PROBLEM]), fontSize: 9, margin: [0, 0, 0, 4]})
  }
  if (tekst(d[ODRADJENO]).trim()) {
    redovi.push({text: 'Opis servisa', fontSize: 8, color: SIVA})
    redovi.push({text: tekst(d[ODRADJENO]), fontSize: 9})
  }
  if (!redovi.length) { return null }
  return {stack: redovi, margin: [0, 0, 0, 8]}
}

const KOLONE_S_CIJENOM = [
  {head: 'Rb.', w: 18, align: 'right'},
  {head: 'Šifra', w: 55, align: 'left'},
  {head: 'Opis', w: '*', align: 'left'},
  {head: 'Kol.', w: 40, align: 'right'},
  {head: 'JM', w: 26, align: 'left'},
  {head: 'Cijena', w: 55, align: 'right'},
  {head: 'Iznos', w: 60, align: 'right'}
]

const KOLONE_BEZ_CIJENE = [
  {head: 'Rb.', w: 18, align: 'right'},
  {head: 'Šifra', w: 55, align: 'left'},
  {head: 'Opis', w: '*', align: 'left'},
  {head: 'Kol.', w: 40, align: 'right'},
  {head: 'JM', w: 26, align: 'left'}
]

const celija = (text, align, stil) => Object.assign({
  text: text, fontSize: 8, alignment: align, margin: [2, 2.5, 2, 2.5]
}, stil || {})

/**
 * Jedna skupina stavaka (usluge ili roba) kao tablica.
 * @param {String} naslov
 * @param {Array} stavke
 * @param {Object} opcije {bezCijena, praznihRedaka, valuta}
 */
const skupina = (naslov, stavke, opcije) => {
  const o = opcije || {}
  const cols = o.bezCijena ? KOLONE_BEZ_CIJENE : KOLONE_S_CIJENOM
  const body = [cols.map(c => celija(c.head, c.align, {bold: true, fontSize: 8}))]

  stavke.forEach((s, i) => {
    const red = [
      celija(String(i + 1), 'right'),
      celija(tekst(s.itemID), 'left'),
      celija(tekst(s.itemName || s.itemDescription), 'left'),
      celija(kolicina(kol(s)), 'right'),
      celija(tekst(s.itemUnit), 'left')
    ]
    if (!o.bezCijena) {
      red.push(celija(novac(s.itemPriceWithVAT), 'right'))
      red.push(celija(novac(iznosStavke(s)), 'right'))
    }
    body.push(red)
  })

  // Prazni redovi: serviser rukom dopisuje sto je odradio ili ugradio.
  for (let i = 0; i < (o.praznihRedaka || 0); i += 1) {
    body.push(cols.map(() => celija(' ', 'left', {margin: [2, 7, 2, 7]})))
  }

  if (!o.bezCijena && stavke.length) {
    const span = cols.length - 1
    const red = [celija('Ukupno ' + naslov.toLowerCase() + ' ' + (o.valuta || 'EUR'), 'right',
      {bold: true, colSpan: span})]
    for (let i = 1; i < span; i += 1) { red.push({text: ''}) }
    red.push(celija(novac(zbroj(stavke)), 'right', {bold: true}))
    body.push(red)
  }

  return [
    {
      text: naslov + '   ',
      fontSize: 10,
      bold: true,
      margin: [0, 8, 0, 3]
    },
    {
      text: stavke.length
        ? stavke.length + (stavke.length === 1 ? ' stavka' : (stavke.length < 5 ? ' stavke' : ' stavaka'))
        : 'Nema stavaka u ovoj skupini.',
      fontSize: 7,
      color: SIVA,
      margin: [0, 0, 0, 3]
    },
    {
      table: {headerRows: 1, widths: cols.map(c => c.w), body: body},
      layout: {
        hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length ? 0.8 : 0.3),
        vLineWidth: () => 0.3,
        hLineColor: () => LINIJA,
        vLineColor: () => LINIJA
      }
    }
  ]
}

const podnozjeA4 = (ctx) => (currentPage, pageCount) => ({
  columns: [
    {text: [(ctx.company || {}).CompanyNameShort || (ctx.company || {}).CompanyName || '',
      ctx.naslov, 'ispis ' + dayjs().format('DD.MM.YYYY HH:mm')].filter(x => x).join(' · '),
      fontSize: 6, color: SIVA, margin: [18, 6, 0, 0]},
    {text: currentPage + '/' + pageCount, fontSize: 6, color: SIVA,
      alignment: 'right', margin: [0, 6, 18, 0]}
  ]
})

const ukupnoSve = (usluge, roba, valuta) => {
  const u = zbroj(usluge)
  const r = zbroj(roba)
  return {
    table: {
      widths: ['*', 80],
      body: [
        [celija('Usluge', 'right'), celija(novac(u), 'right')],
        [celija('Roba i dijelovi', 'right'), celija(novac(r), 'right')],
        [celija('UKUPNO ' + valuta, 'right', {bold: true, fontSize: 10}),
          celija(novac(u + r), 'right', {bold: true, fontSize: 10})]
      ]
    },
    layout: {
      hLineWidth: (i, node) => (i === node.table.body.length - 1 || i === node.table.body.length ? 0.8 : 0),
      vLineWidth: () => 0,
      hLineColor: () => LINIJA
    },
    margin: [0, 10, 0, 0]
  }
}

/**
 * PONUDA ZA SERVIS - usluge i dijelovi odvojeno, s iznosima.
 * @param {Object} ctx {company, document, stavke, valuta, bezCijena}
 */
export const buildServisPonudaPdf = (ctx) => {
  const c = Object.assign({naslov: 'PONUDA ZA SERVIS'}, ctx)
  const {usluge, roba} = podijeliStavke(c.stavke)
  const valuta = c.valuta || 'EUR'
  const sadrzaj = [zaglavljeA4(c), partnerA4(c)]
  const predmet = predmetServisa(c)
  if (predmet) { sadrzaj.push(predmet) }
  const opis = opisServisa(c)
  if (opis) { sadrzaj.push(opis) }
  sadrzaj.push(...skupina('Usluge', usluge, {valuta, bezCijena: c.bezCijena}))
  sadrzaj.push(...skupina('Dijelovi', roba, {valuta, bezCijena: c.bezCijena}))
  if (!c.bezCijena) { sadrzaj.push(ukupnoSve(usluge, roba, valuta)) }
  sadrzaj.push({
    text: 'Ponuda ne obvezuje na izvođenje radova prije potvrde kupca. ' +
      'Količine i dijelovi mogu se promijeniti nakon pregleda.',
    fontSize: 7, color: SIVA, margin: [0, 10, 0, 0]
  })

  return {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: [28, 24, 28, 28],
    content: sadrzaj,
    footer: podnozjeA4(c)
  }
}

/**
 * NALOG ZA SERVIS - usluge i roba odvojeno, svaka skupina s praznim redovima
 * za rucni upis (autoservis).
 * @param {Object} ctx {company, document, stavke, valuta, bezCijena, praznihRedaka}
 */
export const buildServisNalogPdf = (ctx) => {
  const c = Object.assign({naslov: 'NALOG ZA SERVIS', praznihRedaka: 6}, ctx)
  const {usluge, roba} = podijeliStavke(c.stavke)
  const valuta = c.valuta || 'EUR'
  const sadrzaj = [zaglavljeA4(c), partnerA4(c)]
  const predmet = predmetServisa(c)
  if (predmet) { sadrzaj.push(predmet) }
  const opis = opisServisa(c)
  if (opis) { sadrzaj.push(opis) }

  sadrzaj.push(...skupina('Usluge - rad', usluge,
    {valuta, bezCijena: c.bezCijena, praznihRedaka: c.praznihRedaka}))
  sadrzaj.push(...skupina('Roba - ugrađeni dijelovi', roba,
    {valuta, bezCijena: c.bezCijena, praznihRedaka: c.praznihRedaka}))

  // Potpisi: nalog se vraca s radionice, pa mora imati gdje biti potpisan.
  sadrzaj.push({
    columns: [
      {stack: [{text: ' ', fontSize: 9}, {text: '_______________________', fontSize: 9},
        {text: 'Servis obavio', fontSize: 7, color: SIVA}], width: '*'},
      {stack: [{text: ' ', fontSize: 9}, {text: '_______________________', fontSize: 9},
        {text: 'Datum i vrijeme', fontSize: 7, color: SIVA}], width: '*', alignment: 'center'},
      {stack: [{text: ' ', fontSize: 9}, {text: '_______________________', fontSize: 9},
        {text: 'Kupac preuzeo', fontSize: 7, color: SIVA}], width: '*', alignment: 'right'}
    ],
    margin: [0, 18, 0, 0]
  })

  return {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: [28, 24, 28, 28],
    content: sadrzaj,
    footer: podnozjeA4(c)
  }
}

// ---------------------------------------------------------------------------
// Uski (POS) ispis
// ---------------------------------------------------------------------------
//
// Sirina 185 pt i margine 9 pt su iste kao na postojecem POS ispisu servisa
// vozila, da se pisac ponasa jednako. Visina je "auto" - traka se odreze.

const posRed = (lijevo, desno, stil) => ({
  columns: [
    Object.assign({text: lijevo, fontSize: 7, width: '*'}, stil || {}),
    Object.assign({text: desno, fontSize: 7, width: 'auto', alignment: 'right'}, stil || {})
  ]
})

const posCrta = () => ({
  canvas: [{type: 'line', x1: 0, y1: 2, x2: 167, y2: 2, lineWidth: 0.5, lineColor: LINIJA}],
  margin: [0, 1, 0, 1]
})

const posSkupina = (naslov, stavke, bezCijena) => {
  const dio = [{text: naslov, fontSize: 8, bold: true, margin: [0, 4, 0, 1]}]
  if (!stavke.length) {
    dio.push({text: 'nema', fontSize: 7, color: SIVA})
    return dio
  }
  stavke.forEach(s => {
    dio.push({text: tekst(s.itemName || s.itemDescription), fontSize: 7})
    dio.push(posRed(
      kolicina(kol(s)) + ' ' + tekst(s.itemUnit) + (bezCijena ? '' : ' x ' + novac(s.itemPriceWithVAT)),
      bezCijena ? '' : novac(iznosStavke(s)),
      {color: SIVA}
    ))
  })
  if (!bezCijena) {
    dio.push(posRed('Ukupno ' + naslov.toLowerCase(), novac(zbroj(stavke)), {bold: true, fontSize: 7}))
  }
  return dio
}

/**
 * Uski ispis servisnog naloga (POS pisac).
 * @param {Object} ctx {company, document, stavke, valuta, bezCijena}
 */
export const buildServisPosPdf = (ctx) => {
  const c = ctx || {}
  const comp = c.company || {}
  const d = c.document || {}
  const valuta = c.valuta || 'EUR'
  const {usluge, roba} = podijeliStavke(c.stavke)

  const sadrzaj = [
    {text: comp.CompanyNameShort || comp.CompanyName || '', fontSize: 9, bold: true},
    {text: [comp.CompanyStreet, comp.CompanyBuildingNameNumber].filter(x => x).join(' '), fontSize: 7},
    {text: [comp.CompanyPostcode, comp.CompanyCity].filter(x => x).join(' '), fontSize: 7},
    {text: comp.CompanyVATID ? 'OIB: ' + comp.CompanyVATID : '', fontSize: 7},
    // Kontakt servisa na traci: kupac s njom u ruci treba znati koga zvati.
    {text: comp.CompanyPhones ? 'Tel: ' + comp.CompanyPhones : '', fontSize: 7},
    {text: comp.Companyemail ? comp.Companyemail : '', fontSize: 7},
    posCrta(),
    {text: 'SERVISNI NALOG ' + tekst(d.documentId), fontSize: 10, bold: true, margin: [0, 2, 0, 1]},
    {text: 'Datum: ' + datum(d.documentDate) + (d.documentTime ? '  ' + String(d.documentTime).substr(0, 5) : ''), fontSize: 7},
    {text: d.partnerName ? 'Kupac: ' + d.partnerName : '', fontSize: 7},
    {text: d.partnerPhones ? 'Tel: ' + d.partnerPhones : '', fontSize: 7},
    {text: d.partnerVATID ? 'OIB: ' + d.partnerVATID : '', fontSize: 7},
    posCrta(),
    // Predmet servisa je na traci prvo sto treba vidjeti - sto se servisira.
    {text: tekst(d[PREDMET]).trim() ? 'PREDMET: ' + tekst(d[PREDMET]) : '', fontSize: 8, bold: true},
    {text: statusTekst(d) ? 'Status: ' + statusTekst(d) : '', fontSize: 7},
    {text: d.deliveryCurrier ? 'Servis: ' + d.deliveryCurrier : '', fontSize: 7}
  ]

  const opis = [tekst(d[PROBLEM]), tekst(d[ODRADJENO])].filter(x => x.trim()).join(' / ')
  if (opis) {
    sadrzaj.push(posCrta())
    sadrzaj.push({text: opis, fontSize: 7})
  }

  sadrzaj.push(...posSkupina('Usluge', usluge, c.bezCijena))
  sadrzaj.push(...posSkupina('Roba', roba, c.bezCijena))

  if (!c.bezCijena) {
    sadrzaj.push(posCrta())
    sadrzaj.push(posRed('UKUPNO ' + valuta, novac(zbroj(usluge) + zbroj(roba)), {bold: true, fontSize: 9}))
  }
  sadrzaj.push(posCrta())
  sadrzaj.push({text: 'Potpis servisera: ______________', fontSize: 7, margin: [0, 6, 0, 0]})
  sadrzaj.push({text: 'Kupac preuzeo: ______________', fontSize: 7, margin: [0, 6, 0, 0]})

  // QR sa statusom servisa: kupac ga skenira mobitelom i vidi je li gotovo,
  // bez zvanja servisa. Poveznica se gradi iz adrese na kojoj aplikacija radi,
  // pa je na svakom serveru ispravna sama po sebi.
  //
  // Uz kod stoji i ispisana adresa: traka zna doci u ruke bez mobitela s
  // kamerom, a i kad se kod zamaze, adresa se moze prepisati.
  if (c.statusUrl) {
    sadrzaj.push(posCrta())
    sadrzaj.push({text: 'Provjerite status servisa:', fontSize: 7, margin: [0, 4, 0, 2]})
    sadrzaj.push({qr: c.statusUrl, fit: 92, alignment: 'center'})
    sadrzaj.push({text: c.statusUrl, fontSize: 5, alignment: 'center', color: SIVA, margin: [0, 2, 0, 0]})
  }

  return {
    pageSize: {width: 185, height: 'auto'},
    pageOrientation: 'portrait',
    pageMargins: [9, 9, 9, 9],
    content: sadrzaj
  }
}

// ---------------------------------------------------------------------------
// Otvaranje
// ---------------------------------------------------------------------------
//
// Ispis se OTVARA u pregledniku, ne salje se pisacu sam - korisnik vidi sto
// ide na papir i tek onda ispisuje. Automatsko slanje na pisac dolazi poslije.

const otvori = (def) => {
  pdfMake.addVirtualFileSystem(pdfFonts)
  pdfMake.createPdf(def).open()
}

export const printServisPonudaPdf = (ctx) => otvori(buildServisPonudaPdf(ctx))
export const printServisNalogPdf = (ctx) => otvori(buildServisNalogPdf(ctx))
export const printServisPosPdf = (ctx) => otvori(buildServisPosPdf(ctx))

export default {
  podijeliStavke,
  buildServisPonudaPdf, printServisPonudaPdf,
  buildServisNalogPdf, printServisNalogPdf,
  buildServisPosPdf, printServisPosPdf
}
